'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  User,
  Phone,
  Mail,
  MapPin,
  MessageSquare,
  ChevronRight,
  ExternalLink,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui';
import { CommsAction } from '@/components/comms';
import { LOSS_REASONS } from '@/lib/leads/loss-reasons';
import { usePolling } from '@/lib/usePolling';

// SPECS: __tests__/dashboard-lead-inbox.test.tsx
const LEAD_PAGE_SIZE = 6;

interface Lead {
  id: string;
  source_table: 'website_leads' | 'landing_page_leads';
  name: string;
  phone: string | null;
  email: string | null;
  form_type: string;
  services: string[];
  message: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  utm_source: string | null;
  ghl_contact_id: string | null;
  prospect_id: string | null;
  created_at: string;
  status: string;
  lead_notes: string | null;
  all_form_types?: string[];
  all_utm_sources?: string[];
  touchpoint_count?: number;
  first_seen_at?: string;
  follow_up_at: string | null;
  contacted_at: string | null;
  estimate: { id: string; status: string; total_cents: number } | null;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return `${Math.floor(diff / (1000 * 60))}m ago`;
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}


function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { color: string; icon: typeof CheckCircle2; label: string }> = {
    converted: { color: 'emerald', icon: CheckCircle2, label: 'CONVERTED' },
    won: { color: 'emerald', icon: CheckCircle2, label: 'WON' },
    contacted: { color: 'blue', icon: Phone, label: 'CONTACTED' },
    follow_up: { color: 'purple', icon: Clock, label: 'FOLLOW UP' },
    estimate_sent: { color: 'blue', icon: FileText, label: 'ESTIMATE SENT' },
    lost: { color: 'zinc', icon: AlertTriangle, label: 'LOST' },
  };

  if (status.startsWith('estimate_')) {
    const est = status.replace('estimate_', '');
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
        <FileText className="w-3 h-3" /> {est.toUpperCase()}
      </span>
    );
  }

  const c = config[status];
  if (c) {
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-${c.color}-400 bg-${c.color}-500/10 px-2 py-0.5 rounded-full`}>
        <Icon className="w-3 h-3" /> {c.label}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
      <Clock className="w-3 h-3" /> NEW
    </span>
  );
}

function LeadCard({ lead, onUpdate }: { lead: Lead; onUpdate: (_id: string, _data: Record<string, unknown>) => void }) {
  const address = [lead.street, lead.city, lead.state].filter(Boolean).join(', ');
  // Closing as lost takes two deliberate steps: open the picker, choose a
  // reason, then confirm. A single mis-tap must never lose a lead.
  const [closingLost, setClosingLost] = useState(false);
  const [lossReason, setLossReason] = useState('');
  const isLandingPageLead = lead.source_table === 'landing_page_leads';

  async function markLandingLeadContacted() {
    try {
      const response = await fetch('/api/leads/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: lead.id, lead_table: 'landing_page_leads' }),
      });
      if (response.ok) onUpdate(lead.id, { contacted_at: new Date().toISOString() });
    } catch {
      // Keep the lead visible when the source-specific stamp did not complete.
    }
  }

  return (
    <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-zinc-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-white truncate">{lead.name}</h3>
            <p className="text-[10px] text-zinc-500">{timeAgo(lead.created_at)} · {lead.form_type.replace(/-/g, ' ')}</p>
          </div>
        </div>
        <StatusBadge status={lead.status} />
      </div>

      {/* Contact info */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-xs text-zinc-400">
        {lead.phone && (
          <CommsAction
            phone={lead.phone}
            contactId={lead.ghl_contact_id ?? undefined}
            entityType={lead.source_table === 'landing_page_leads' ? 'landing_page_lead' : 'lead'}
            entityId={lead.id}
            compact
          />
        )}
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-white transition-colors truncate">
            <Mail className="w-3 h-3" /> {lead.email}
          </a>
        )}
        {address && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {address}
          </span>
        )}
      </div>

      {/* Services wanted */}
      {Array.isArray(lead.services) && lead.services.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {lead.services.map((s, i) => (
            <span key={i} className="text-[10px] font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Message */}
      {lead.message && lead.message.length > 3 && (
        <div className="flex items-start gap-1.5 mb-3 text-xs text-zinc-400 bg-zinc-800/50 rounded-lg px-3 py-2">
          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <p className="line-clamp-2">{lead.message}</p>
        </div>
      )}

      {/* Attribution */}
      {(lead.all_form_types || lead.utm_source) && (
        <div className="flex flex-wrap gap-1 mb-2">
          {(lead.all_form_types || [lead.form_type]).map((ft: string, i: number) => (
            <span key={`ft-${i}`} className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-400 rounded">
              {ft.replace(/-/g, ' ').replace(/_/g, ' ')}
            </span>
          ))}
          {(lead.all_utm_sources || (lead.utm_source ? [lead.utm_source] : [])).map((src: string, i: number) => (
            <span key={`src-${i}`} className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-blue-500/15 text-blue-400 rounded">
              {src}
            </span>
          ))}
          {(lead.touchpoint_count || 0) > 1 && (
            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 rounded">
              {lead.touchpoint_count} touchpoints
            </span>
          )}
        </div>
      )}

      {/* Follow-up reminder */}
      {lead.follow_up_at && (
        <p className="text-[10px] text-purple-400 mb-2">📅 Follow up: {new Date(lead.follow_up_at).toLocaleDateString()}</p>
      )}

      {/* Notes */}
      {lead.lead_notes && (
        <p className="text-[10px] text-zinc-500 italic mb-2">📝 {lead.lead_notes}</p>
      )}

      {/* Close-as-lost confirmation: reason required */}
      {closingLost && (
        <div className="mt-2 space-y-2 rounded-lg border border-rose-500/20 bg-rose-500/5 p-2">
          <p className="text-xs text-zinc-300">Close {lead.name} as lost?</p>
          <Select
            aria-label={`Why is ${lead.name} lost?`}
            value={lossReason}
            onChange={e => setLossReason(e.target.value)}
            className="w-full text-xs"
          >
            <option value="">Why is this lead lost?</option>
            {LOSS_REASONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </Select>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!lossReason}
              className="px-3 py-1.5 bg-rose-600 text-white text-xs font-bold rounded-lg disabled:opacity-40"
              onClick={() => {
                if (!lossReason) return;
                onUpdate(lead.id, { lead_status: 'lost', close_reason: lossReason });
              }}
            >
              Confirm lost
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-zinc-200 text-xs px-2 py-1"
              onClick={() => { setClosingLost(false); setLossReason(''); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5 flex-wrap">
        {isLandingPageLead && !lead.contacted_at && (
          <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1"
            onClick={() => void markLandingLeadContacted()}>
            ✓ Contacted
          </Button>
        )}
        {/* Status actions */}
        {!isLandingPageLead && lead.status === 'new' && (
          <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1"
            onClick={() => onUpdate(lead.id, { lead_status: 'contacted' })}>
            ✓ Contacted
          </Button>
        )}
        {!isLandingPageLead && (lead.status === 'new' || lead.status === 'contacted') && (
          <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 text-xs px-2 py-1"
            onClick={() => {
              const days = 2;
              const followUp = new Date(Date.now() + days * 86400000).toISOString();
              onUpdate(lead.id, { lead_status: 'follow_up', follow_up_at: followUp });
            }}>
            📅 Follow up
          </Button>
        )}
        {!isLandingPageLead && lead.status !== 'lost' && lead.status !== 'won' && !closingLost && (
          <Button variant="ghost" size="sm" className="text-zinc-600 hover:text-zinc-400 text-xs px-2 py-1"
            onClick={() => setClosingLost(true)}>
            Close as lost…
          </Button>
        )}

        {/* Navigation actions */}
        {!isLandingPageLead && !lead.estimate && lead.status !== 'lost' && (
          <Link
            href={`/estimates/unified${lead.ghl_contact_id ? `?contact_id=${lead.ghl_contact_id}` : ''}`}
            className="flex items-center gap-1 text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors ml-auto"
          >
            <FileText className="w-3.5 h-3.5" /> Create Estimate
          </Link>
        )}
        {lead.estimate && (
          <Link
            href={`/estimates/unified/${lead.estimate.id}`}
            className="flex items-center gap-1 text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors ml-auto"
          >
            <FileText className="w-3.5 h-3.5" /> View Estimate
          </Link>
        )}
        {lead.ghl_contact_id && (
          <a
            href={`https://app.gohighlevel.com/v2/location/${process.env.NEXT_PUBLIC_GHL_LOCATION_ID || ''}/contacts/${lead.ghl_contact_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> GHL
          </a>
        )}
      </div>
    </div>
  );
}

export function LeadInbox() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [visibleCount, setVisibleCount] = useState(LEAD_PAGE_SIZE);
  const { data, isLoading, error, lastUpdated, refresh } = usePolling<{ leads: Lead[] } | null>({
    url: '/api/dashboard/leads?days=14&limit=40',
    intervalMs: 30000,
    initialValue: null,
  });

  useEffect(() => {
    if (data) setLeads(data.leads);
  }, [data]);

  async function handleUpdate(id: string, data: Record<string, unknown>) {
    setActionError(null);
    const lead = leads.find((row) => row.id === id);
    if (lead?.source_table === 'landing_page_leads') {
      if (typeof data.contacted_at === 'string') {
        setLeads((prev) => prev.filter((row) => row.id !== id));
      }
      return;
    }
    try {
      const res = await fetch(`/api/dashboard/leads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Could not save this lead. Please try again.');
      const result = await res.json();
      // Update local state
      setLeads(prev => {
        const newStatus = (data.lead_status as string) || '';
        if (newStatus === 'lost' || newStatus === 'won') {
          // Remove from list
          return prev.filter(l => l.id !== id);
        }
        return prev.map(l => l.id === id ? { ...l, ...result.lead, status: result.lead.lead_status || l.status } : l);
      });
      // Cancel any read started before the save and refill the visible list.
      void refresh();
    } catch {
      setActionError('Could not save this lead. Please try again.');
    }
  }

  const newCount = leads.filter(l => l.status === 'new').length;
  const latestLead = leads.reduce<Lead | null>((latest, lead) =>
    !latest || Date.parse(lead.created_at) > Date.parse(latest.created_at) ? lead : latest, null);

  return (
    <section aria-label="Lead Inbox" className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <Button
          variant="ghost"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-controls="lead-inbox-content"
          className="flex items-center gap-2 px-0 hover:bg-transparent"
        >
          <User className="w-5 h-5 text-amber-400" />
          <span className="text-sm font-bold text-white">Lead Inbox</span>
          {newCount > 0 && (
            <span className="text-[10px] font-bold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-full">
              {newCount} new
            </span>
          )}
          <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
        </Button>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="min-h-11" onClick={() => void refresh()}>
            <RefreshCw className="h-4 w-4" /> Refresh leads
          </Button>
          <Link href="/marketing/leads" className="inline-flex min-h-11 items-center text-xs text-zinc-400 hover:text-white">
            View all leads →
          </Link>
        </div>
      </div>

      {error && (
        <p role="alert" className="mb-3 text-sm text-amber-300">
          {lastUpdated ? 'Lead refresh failed. Showing the last loaded leads.' : 'Could not load leads.'} Use Refresh leads to retry.
        </p>
      )}
      {actionError && <p role="alert" className="mb-3 text-sm text-rose-300">{actionError}</p>}
      {lastUpdated && (
        <p className="mb-3 text-xs text-zinc-400">
          Updated <time dateTime={lastUpdated.toISOString()}>{lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })} CT</time>
          {latestLead && <> · Newest loaded lead {new Date(latestLead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' })}</>}
        </p>
      )}
      {!collapsed && (
        <div id="lead-inbox-content">
          {!data && !error && (
            <p role="status" className="flex items-center gap-2 text-sm text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" /> {isLoading ? 'Loading leads…' : 'Waiting to load leads…'}
            </p>
          )}
          {data && leads.length === 0 && !error && <p className="text-sm text-zinc-400">No open leads in the recent inbox. View all leads for older requests.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {leads.slice(0, visibleCount).map(lead => (
              <LeadCard key={lead.id} lead={lead} onUpdate={handleUpdate} />
            ))}
          </div>
          {leads.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-zinc-400">Showing {Math.min(visibleCount, leads.length)} of {leads.length} loaded leads. Older leads are on the full board.</p>
              {visibleCount < leads.length && (
                <Button variant="secondary" className="min-h-11" onClick={() => setVisibleCount(count => count + LEAD_PAGE_SIZE)}>Show more leads</Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
